import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Video } from '@meradio/shared-types';

export type SerializableVideo = Omit<Video, 'createdAt' | 'updatedAt' | 'publishedAt' | 'dateAdded'> & {
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  dateAdded: string;
};

export const serializeVideo = (video: Video): SerializableVideo => {
  const { createdAt, updatedAt, publishedAt, dateAdded, ...rest } = video;

  return {
    ...rest,
    createdAt: typeof createdAt === 'string' ? createdAt : createdAt.toISOString(),
    updatedAt: typeof updatedAt === 'string' ? updatedAt : updatedAt.toISOString(),
    publishedAt: typeof publishedAt === 'string' ? publishedAt : publishedAt.toISOString(),
    dateAdded: typeof dateAdded === 'string' ? dateAdded : dateAdded.toISOString(),
  };
};

export interface PlayerState {
  currentVideo: SerializableVideo | null;
  isPlaying: boolean;
  queue: SerializableVideo[];
  queueIndex: number;

  repeatMode: 'off' | 'one' | 'all';
  shuffleMode: boolean;

  volume: number;
  isMuted: boolean;

  currentTime: number;
  duration: number;

  isLoading: boolean;
  error: string | null;

  originalQueue: SerializableVideo[];
}

const initialState: PlayerState = {
  currentVideo: null,
  isPlaying: false,
  queue: [],
  queueIndex: -1,
  repeatMode: 'off',
  shuffleMode: false,
  volume: 100,
  isMuted: false,
  currentTime: 0,
  duration: 0,
  isLoading: false,
  error: null,
  originalQueue: [],
};

const playerSlice = createSlice({
  name: 'player',
  initialState,
  reducers: {
    play: (state) => {
      state.isPlaying = true;
      state.error = null;
    },

    pause: (state) => {
      state.isPlaying = false;
    },

    stop: (state) => {
      state.isPlaying = false;
      state.currentTime = 0;
    },

    setCurrentVideo: (state, action: PayloadAction<Video | null>) => {
      console.log('🎵 Setting current video:', action.payload);

      if (!action.payload) {
        state.currentVideo = null;
        state.currentTime = 0;
        state.duration = 0;
        state.isLoading = false;
        state.error = null;
        return;
      }

      state.currentVideo = serializeVideo(action.payload);
      state.currentTime = 0;
      state.duration = 0;
      state.isLoading = true;
      state.error = null;
    },

    updateProgress: (state, action: PayloadAction<{ currentTime: number; duration: number }>) => {
      state.currentTime = action.payload.currentTime;
      state.duration = action.payload.duration;
      state.isLoading = false;
    },

    seekTo: (state, action: PayloadAction<number>) => {
      state.currentTime = action.payload;
    },

    setVolume: (state, action: PayloadAction<number>) => {
      state.volume = Math.max(0, Math.min(100, action.payload));
      if (state.volume > 0) {
        state.isMuted = false;
      }
    },

    toggleMute: (state) => {
      state.isMuted = !state.isMuted;
    },

    setMuted: (state, action: PayloadAction<boolean>) => {
      state.isMuted = action.payload;
    },

    addToQueue: (state, action: PayloadAction<Video>) => {
      const video = action.payload;
      if (!state.queue.find(v => v.videoId === video.videoId)) {
        state.queue.push(serializeVideo(video));
        if (!state.shuffleMode) {
          state.originalQueue = [...state.queue];
        }
      }
    },

    addMultipleToQueue: (state, action: PayloadAction<Video[]>) => {
      const videos = action.payload;
      videos.forEach(video => {
        if (!state.queue.find(v => v.videoId === video.videoId)) {
          state.queue.push(serializeVideo(video));
        }
      });
      if (!state.shuffleMode) {
        state.originalQueue = [...state.queue];
      }
    },

    removeFromQueue: (state, action: PayloadAction<string>) => {
      const videoId = action.payload;
      state.queue = state.queue.filter(v => v.videoId !== videoId);
      state.originalQueue = state.originalQueue.filter(v => v.videoId !== videoId);

      if (state.queueIndex >= state.queue.length) {
        state.queueIndex = state.queue.length - 1;
      }
    },

    clearQueue: (state) => {
      state.queue = [];
      state.originalQueue = [];
      state.queueIndex = -1;
    },

    setQueue: (state, action: PayloadAction<Video[]>) => {
      const serializedQueue = action.payload.map(video => serializeVideo(video));
      state.queue = serializedQueue;
      state.originalQueue = [...serializedQueue];
      state.queueIndex = 0;
    },

    playVideo: (state, action: PayloadAction<Video>) => {
      const video = action.payload;
      const serializedVideo = serializeVideo(video);
      state.currentVideo = serializedVideo;
      state.isPlaying = true;
      state.currentTime = 0;
      state.duration = 0;
      state.isLoading = true;
      state.error = null;

      const queueIndex = state.queue.findIndex(v => v.videoId === video.videoId);
      if (queueIndex >= 0) {
        state.queueIndex = queueIndex;
      } else {
        state.queue.unshift(serializedVideo);
        state.originalQueue.unshift(serializedVideo);
        state.queueIndex = 0;
      }
    },

    playNext: (state) => {
      if (state.queue.length === 0) return;

      let nextIndex = state.queueIndex + 1;

      if (nextIndex >= state.queue.length) {
        if (state.repeatMode === 'all') {
          nextIndex = 0;
        } else if (state.repeatMode === 'one') {
          nextIndex = state.queueIndex;
        } else {
          state.isPlaying = false;
          return;
        }
      }

      const nextVideo = state.queue[nextIndex];
      if (nextVideo) {
        state.currentVideo = nextVideo;
        state.queueIndex = nextIndex;
        state.isPlaying = true;
        state.currentTime = 0;
        state.duration = 0;
        state.isLoading = true;
        state.error = null;
      }
    },

    playPrevious: (state) => {
      if (state.queue.length === 0) return;

      let prevIndex = state.queueIndex - 1;

      if (prevIndex < 0) {
        if (state.repeatMode === 'all') {
          prevIndex = state.queue.length - 1;
        } else {
          prevIndex = 0;
        }
      }

      const prevVideo = state.queue[prevIndex];
      if (prevVideo) {
        state.currentVideo = prevVideo;
        state.queueIndex = prevIndex;
        state.isPlaying = true;
        state.currentTime = 0;
        state.duration = 0;
        state.isLoading = true;
        state.error = null;
      }
    },

    setRepeatMode: (state, action: PayloadAction<'off' | 'one' | 'all'>) => {
      state.repeatMode = action.payload;
    },

    toggleRepeatMode: (state) => {
      const modes: ('off' | 'one' | 'all')[] = ['off', 'one', 'all'];
      const currentIndex = modes.indexOf(state.repeatMode);
      const nextIndex = (currentIndex + 1) % modes.length;
      state.repeatMode = modes[nextIndex];
    },

    setShuffleMode: (state, action: PayloadAction<boolean>) => {
      state.shuffleMode = action.payload;

      if (action.payload) {
        state.originalQueue = [...state.queue];
        const shuffledQueue = [...state.queue];

        for (let i = shuffledQueue.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffledQueue[i], shuffledQueue[j]] = [shuffledQueue[j], shuffledQueue[i]];
        }

        state.queue = shuffledQueue;

        if (state.currentVideo) {
          state.queueIndex = state.queue.findIndex(v => v.videoId === state.currentVideo!.videoId);
        }
      } else {
        state.queue = [...state.originalQueue];

        if (state.currentVideo) {
          state.queueIndex = state.queue.findIndex(v => v.videoId === state.currentVideo!.videoId);
        }
      }
    },

    toggleShuffleMode: (state) => {
      const newShuffleMode = !state.shuffleMode;
      playerSlice.caseReducers.setShuffleMode(state, {
        type: 'player/setShuffleMode',
        payload: newShuffleMode
      });
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.isLoading = false;
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    resetPlayer: () => initialState,
  },
});

export const {
  play,
  pause,
  stop,
  setCurrentVideo,
  updateProgress,
  seekTo,
  setVolume,
  toggleMute,
  setMuted,
  addToQueue,
  addMultipleToQueue,
  removeFromQueue,
  clearQueue,
  setQueue,
  playVideo,
  playNext,
  playPrevious,
  setRepeatMode,
  toggleRepeatMode,
  setShuffleMode,
  toggleShuffleMode,
  setError,
  setLoading,
  resetPlayer,
} = playerSlice.actions;

export const selectPlayerState = (state: { player: PlayerState }) => state.player;
export const selectCurrentVideo = (state: { player: PlayerState }) => state.player.currentVideo;
export const selectIsPlaying = (state: { player: PlayerState }) => state.player.isPlaying;
export const selectQueue = (state: { player: PlayerState }) => state.player.queue;
export const selectQueueIndex = (state: { player: PlayerState }) => state.player.queueIndex;
export const selectRepeatMode = (state: { player: PlayerState }) => state.player.repeatMode;
export const selectShuffleMode = (state: { player: PlayerState }) => state.player.shuffleMode;
export const selectVolume = (state: { player: PlayerState }) => state.player.volume;
export const selectIsMuted = (state: { player: PlayerState }) => state.player.isMuted;
export const selectCurrentTime = (state: { player: PlayerState }) => state.player.currentTime;
export const selectDuration = (state: { player: PlayerState }) => state.player.duration;
export const selectIsLoading = (state: { player: PlayerState }) => state.player.isLoading;
export const selectPlayerError = (state: { player: PlayerState }) => state.player.error;

export default playerSlice.reducer;
